import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { type ResourceConfig, ResourcePage, fmtDate } from "@/components/resource-page";
import type { City } from "@/lib/geo";
import type { TranslationKey } from "@/lib/i18n";
import { useTranslation } from "@/stores/locale";

export const Route = createFileRoute("/_app/cities")({ component: CitiesPage });

const build = (t: (key: TranslationKey) => string): ResourceConfig<City> => ({
  title: t("geo.cities"),
  singular: t("geo.city"),
  plural: t("geo.cities"),
  endpoint: "/cities",
  model: "city",
  columns: [
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (row) => (
        <span className="text-accent underline underline-offset-4">{row.id}</span>
      ),
    },
    { key: "name", label: t("geo.name"), sortKey: "name", render: (row) => row.name },
    {
      key: "code",
      label: t("geo.code"),
      sortKey: "code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    { key: "province", label: t("geo.province"), render: (row) => row.province_name ?? "—" },
    {
      key: "population",
      label: t("geo.population"),
      sortKey: "population",
      render: (row) => row.population ?? "—",
    },
    { key: "latitude", label: t("geo.latitude"), render: (row) => row.latitude ?? "—" },
    { key: "longitude", label: t("geo.longitude"), render: (row) => row.longitude ?? "—" },
    {
      key: "created_by",
      label: t("geo.createdBy"),
      render: (row, lookup) => lookup("created_by_id", row.created_by_id),
    },
    {
      key: "created_at",
      label: t("geo.createdAt"),
      sortKey: "created_at",
      render: (row) => fmtDate(row.created_at),
    },
  ],
  defaultSort: { key: "id", order: "desc" },
  filters: [
    {
      name: "created_by_id",
      label: t("geo.createdByFilter"),
      type: "fk",
      endpoint: "/users/compact",
    },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
    {
      name: "province_id",
      label: t("geo.province"),
      type: "fk",
      endpoint: "/provinces/compact",
    },
  ],
  fields: [
    { name: "name", label: t("geo.name"), required: true },
    { name: "code", label: t("geo.code"), required: true },
    {
      name: "province_id",
      label: t("geo.province"),
      type: "fk",
      required: true,
      endpoint: "/provinces/compact",
    },
    { name: "population", label: t("geo.population"), type: "number", min: 0, required: true },
    {
      name: "latitude",
      required: true,
      label: t("geo.latitude"),
      type: "number",
      step: "any",
      min: -90,
      max: 90,
    },
    {
      name: "longitude",
      required: true,
      label: t("geo.longitude"),
      type: "number",
      step: "any",
      min: -180,
      max: 180,
    },
  ],
  detailLabels: {
    population: t("geo.population"),
    latitude: t("geo.latitude"),
    longitude: t("geo.longitude"),
    code: t("geo.code"),
    province_name: t("geo.province"),
  },
  toForm: (row) => ({
    name: row.name,
    code: row.code,
    province_id: String(row.province_id),
    population: row.population?.toString() ?? "",
    latitude: row.latitude?.toString() ?? "",
    longitude: row.longitude?.toString() ?? "",
  }),
});

function CitiesPage() {
  const t = useTranslation();
  const config = useMemo(() => build(t), [t]);
  return <ResourcePage config={config} />;
}
